/**
 * parsePlanError — normalizes a 403 API error into a plan/upgrade descriptor.
 *
 * The backend returns two shapes of 403 for gated/limited features:
 *   1. Plan gate (middleware/plan_gate.py): detail is an object
 *      { message, current_plan, required_plan, upgrade_url }
 *   2. Limit reached (e.g. cadences.py max_cadences): detail is a plain string
 *      "Cadence limit reached (3). Upgrade your plan for more cadences."
 *
 * Returns a normalized object when the error is plan/limit related, else null.
 * Use this everywhere instead of a generic `toast.error('Failed ...')` so users
 * always get the real reason + an upgrade path.
 */
export function parsePlanError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status !== 403 || !detail) return null;

  // Shape 1: structured plan-gate object
  if (typeof detail === 'object' && (detail.required_plan || detail.current_plan)) {
    return {
      isPlanGate: true,
      message: detail.message || `This feature requires the ${detail.required_plan} plan.`,
      currentPlan: detail.current_plan || null,
      requiredPlan: detail.required_plan || null,
      upgradeUrl: detail.upgrade_url || '/billing',
    };
  }

  // Shape 2: a limit-reached string (still a billing wall → offer upgrade)
  if (typeof detail === 'string' && /limit reached|upgrade your plan/i.test(detail)) {
    return {
      isPlanGate: false,
      message: detail,
      currentPlan: null,
      requiredPlan: null,
      upgradeUrl: '/billing',
    };
  }

  return null;
}

export default parsePlanError;
